"use client"

import { useEffect, useRef, useState } from "react"
import { AlertTriangle, Check } from "lucide-react"
import { ToolPageShell, ToolProductCarousel, ToolShareBar } from "@/components/brand-kit"
import { GuideTool } from "@/components/tools/guide-tool"

// Sources:
//   • Whirlpool, GE, LG, Samsung, Frigidaire installation guides:
//     refrigerators must be level side-to-side and tilted slightly back
//     (typ. 1/4" to 1/2" higher in front than back) so doors auto-close.
//     Adjustable front leveling legs and rear roller adjusters.
//   • ASHRAE Refrigeration Handbook: hermetic compressors require correct
//     orientation for oil return — out-of-level operation can starve the
//     compressor of lubrication.
//   • Manufacturer guidance: water lines (ice maker / dispenser) require a
//     level cabinet to drain correctly. Ice maker fill cups overflow if the
//     unit tilts left/right.

const SECTIONS = [
  { id: "why", t: "Why level matters" },
  { id: "tools", t: "Tools you need" },
  { id: "level", t: "Leveling steps" },
  { id: "verify", t: "Verify the result" },
  { id: "trouble", t: "If it won't sit right" },
] as const

export function LevelingBalanceGuidePageClient() {
  const [active, setActive] = useState<string>(SECTIONS[0].id)
  const observerRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect()
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
        if (visible.length > 0) {
          const topmost = visible.reduce((prev, curr) =>
            prev.boundingClientRect.top < curr.boundingClientRect.top ? prev : curr,
          )
          setActive(topmost.target.id)
        }
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    )
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id)
      if (el) observerRef.current?.observe(el)
    })
    return () => observerRef.current?.disconnect()
  }, [])

  const jumpTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    const y = el.getBoundingClientRect().top + window.scrollY - 96
    window.scrollTo({ top: y, behavior: "smooth" })
    setActive(id)
    // Keep the URL hash in sync so the entry is a real, shareable anchor
    // (plan §0 item 179a). replaceState avoids the native hash-jump.
    history.replaceState(null, "", `#${id}`)
  }

  return (
    <ToolPageShell
      toolName="Refrigerator Leveling Guide"
      heroEyebrow="Installation"
      heroTitlePre="Get the doors to"
      heroTitleItalic="self-close"
      heroTitlePost="every time."
      heroSubtitle="Manufacturers ship every refrigerator with adjustable front legs for the same reason: the unit needs to sit level side-to-side and tipped slightly back so the doors auto-close, the compressor lubricates correctly, and the ice maker fills evenly."
      heroMeta={["⏱ ~5 min read", "Manufacturer install guides"]}
      heroShare={<ToolShareBar title="Refrigerator Leveling Guide" />}
      heroRight={
        <div className="rounded-xl p-6 bg-gray-900 text-white">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-4 text-[#ff9951]">
            The leveling spec
          </div>
          <p
            className="text-[17px] font-medium leading-[1.45] mb-5"
            style={{ textWrap: "balance" as never }}
          >
            Side-to-side: <em className="italic text-[#ff9951]">level</em>. Front-to-back:{" "}
            <em className="italic text-[#ff9951]">front 1/4&quot;–1/2&quot; higher than back</em>{" "}
            so doors self-close.
          </p>
          <div className="grid grid-cols-2 gap-3 pt-4 border-t border-white/10">
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 font-bold">
                Side-to-side
              </div>
              <div className="text-[22px] font-black tracking-tight">0°</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 font-bold">
                Tilt back
              </div>
              <div className="text-[22px] font-black tracking-tight">¼–½&quot;</div>
            </div>
          </div>
        </div>
      }
      faqs={[
        {
          question: "Why does the front need to sit higher than the back?",
          answer:
            "Gravity does the door-closing work. Manufacturer installation guides specify a slight backward tilt — typically 1/4 to 1/2 inch higher in front — so a door opened to about 45° swings closed on its own. Without that tilt, the door rests at whatever angle you left it, and the gasket can't seal.",
        },
        {
          question: "Does an unlevel fridge actually damage the compressor?",
          answer:
            "It can. Hermetic refrigeration compressors rely on oil distributed by the running motion of the compressor and gravity-assisted return. ASHRAE refrigeration practice flags severe out-of-level operation as a contributor to oil migration and inadequate lubrication. The gentle backward tilt manufacturers specify is well within tolerance — significant left/right tilt is the failure case.",
        },
        {
          question: "My ice maker overflows — could leveling fix it?",
          answer:
            "Often, yes. Ice maker fill cups and water dispenser lines need the cabinet level side-to-side. If one side is lower, water pools and the cup overfills before the cycle ends. Level the cabinet, then run an empty cycle to verify.",
        },
        {
          question: "Where exactly do I put the level?",
          answer:
            "Top of the cabinet (not on the doors), running both ways — left-to-right and front-to-back. Doors warp slightly under their own weight and aren't a reliable reference. If your model has a leveling-feet access kick plate, the manufacturer's reference surface is usually marked there.",
        },
        {
          question: "Can I level a built-in or counter-depth model the same way?",
          answer:
            "The principle is identical, but built-in and counter-depth refrigerators often have additional adjustable rear rollers and front leveling legs accessible only after removing the toe-kick. Always work from the model-specific install guide.",
        },
      ]}
      relatedTools={[
        { title: "Door Seal Test", category: "Maintenance", description: "Verify the gasket holds.", href: "/tools/refrigerator-door-seal-test", time: "~3 min" },
        { title: "Maintenance Schedule", category: "Maintenance", description: "When to check level routinely.", href: "/tools/refrigerator-maintenance-schedule", time: "~5 min" },
        { title: "Refrigerator Lifespan", category: "Specialty", description: "Years left on the unit.", href: "/tools/refrigerator-lifespan-calculator", time: "~45 sec" },
        { title: "Kitchen Space Planner", category: "Sizing", description: "Floor flatness and clearance.", href: "/tools/kitchen-space-planner", time: "~2 min" },
      ]}
      cta={{
        title: "Floor too uneven for the leveling legs to handle?",
        body: "Counter-depth refrigerators sit shallower and tolerate a wider range of floor flatness. Worth a look if you're fighting your install.",
        buttonLabel: "Browse counter-depth refrigerators",
        buttonHref: "/counter-depth-refrigerators",
      }}
    >
      <section className="py-8 md:py-12 bg-white">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 grid md:grid-cols-[170px_1fr] gap-8 md:gap-10">
          <aside className="hidden md:block">
            <div className="sticky top-[96px]">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                On this page
              </div>
              <nav className="border-l border-gray-200" aria-label="Section navigation">
                {SECTIONS.map((s) => {
                  const isActive = active === s.id
                  return (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      onClick={(e) => {
                        e.preventDefault()
                        jumpTo(s.id)
                      }}
                      aria-current={isActive ? "location" : undefined}
                      className={[
                        "block cursor-pointer py-2 pl-3 -ml-px transition-all text-left w-full",
                        isActive ? "text-gray-900" : "text-gray-500 hover:text-gray-700",
                      ].join(" ")}
                      style={{
                        borderLeft: isActive ? "2px solid #ff9951" : "2px solid transparent",
                      }}
                    >
                      <div className="text-[13px] font-bold leading-tight">{s.t}</div>
                    </a>
                  )
                })}
              </nav>
            </div>
          </aside>

          <article>
            <GuideTool
              eyebrow="Interactive diagnosis"
              title="Diagnose the level problem"
              intro="The symptom tells you which feet to turn. Three questions, then the exact adjustment."
              questions={[
                            {
                              id: "symptom",
                              label: "What is the door doing?",
                              options: [
                                { value: "swings", label: "Swings open on its own" },
                                { value: "noclose", label: "Won't swing closed on its own" },
                                { value: "rocks", label: "Cabinet rocks or vibrates" }
                              ],
                            },
                            {
                              id: "floor",
                              label: "What's the floor like?",
                              options: [
                                { value: "level", label: "Hard and level" },
                                { value: "sloped", label: "Sloped or uneven" }
                              ],
                            },
                            {
                              id: "tilt",
                              label: "Put a level on top \u2014 which way does it lean?",
                              options: [
                                { value: "forward", label: "Leans forward" },
                                { value: "back", label: "Leans back" },
                                { value: "side", label: "Side to side" }
                              ],
                            }
              ]}
              compute={(a) => {
                            if (a.symptom === "rocks") {
                              return { tone: "warn", verdict: "One foot is off the floor", detail: "Rocking means a corner is unloaded. Find it by pressing each front corner, then extend that side's leveling foot until the rock disappears" + (a.floor === "sloped" ? " \u2014 on an uneven floor, shim the rear roller side if the feet run out of travel." : ".") }
                            }
                            if (a.symptom === "swings" || a.tilt === "forward") {
                              return { tone: "warn", verdict: "Raise the front feet", detail: "Doors that drift open mean the cabinet leans forward. Screw both front feet DOWN (extending them) about a quarter turn at a time until the top leans back roughly a quarter inch \u2014 doors should then swing gently closed from halfway." }
                            }
                            if (a.symptom === "noclose" && a.tilt === "back") {
                              return { tone: "warn", verdict: "Back off the front feet slightly", detail: "Self-closing needs only a slight rearward tilt. If doors slam or the cabinet leans back hard, raise the rear (or lower the front feet) until closing is gentle, not forceful \u2014 excess tilt strains hinges and gaskets." }
                            }
                            return { tone: "good", verdict: "Set the standard quarter-inch tilt", detail: "Level side to side first, then set a slight rearward lean (about a quarter inch across the top). That is the manufacturer spec that makes doors self-close and lets the drain pan sit right." }
                          }}
              ctaLabel="Diagnose it"
              footnote="Adjustment spec: the quarter-inch rearward tilt in the guide below."
            />
            <section id="why" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Why a level fridge actually matters
              </h2>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                Three systems depend on a correctly leveled cabinet: the doors, the refrigeration
                loop, and the water plumbing. Manufacturer installation guides specify the same
                geometry across brands — level side-to-side, tipped slightly back. That tilt is
                what makes the doors auto-close.
              </p>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                A door that doesn&apos;t auto-close eventually doesn&apos;t fully seal — and an
                unsealed door drives the interior toward USDA&apos;s 40°F threshold while running
                the compressor harder than designed.
              </p>

              <div
                className="rounded-xl p-5 md:p-6 my-8 flex gap-4 md:gap-5"
                style={{ background: "rgba(255,153,81,0.06)", border: "1px solid rgba(255,153,81,0.25)" }}
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#ff9951] text-[var(--color-brand-action-text)] flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-black text-[17px] mb-1 text-gray-900">
                    The 45° auto-close test
                  </div>
                  <p className="text-[14px] text-gray-700 leading-[1.6]">
                    Open each door to roughly 45° and let go. A correctly leveled refrigerator
                    swings the door closed under its own weight. If it stays open or swings
                    farther, the front isn&apos;t high enough.
                  </p>
                </div>
              </div>
            </section>

            <section id="tools" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Tools you need
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  "Bubble level (24\" minimum) or a phone with a level app",
                  "Open-end wrench or socket — size in your install guide (often 1/4\" or 5/16\")",
                  "Phillips screwdriver for the toe-kick (built-ins)",
                  "Flashlight",
                  "Thin shim stock — only if floor is uneven beyond leg range",
                  "Helper for tipping the cabinet on built-ins",
                ].map((t) => (
                  <div key={t} className="flex items-start gap-3 p-4 rounded-lg bg-gray-50">
                    <div className="w-6 h-6 rounded-full bg-[#0469f0] text-white flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-[14px] text-gray-700 leading-[1.5]">{t}</span>
                  </div>
                ))}
              </div>
            </section>

            <section id="level" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Leveling, step by step
              </h2>
              <ol className="list-none p-0 m-0 border-t border-gray-900">
                {[
                  { n: "01", t: "Empty the doors and pull off the kick plate", d: "Remove door bins so swinging the doors won't change the load. On most freestanding units, the leveling legs and rear rollers are accessible by snapping off the front kick plate at the bottom." },
                  { n: "02", t: "Level side-to-side first", d: "Place the level across the top of the cabinet, parallel to the front. Turn the front leveling legs (clockwise = raise) until the bubble centers. Both front legs should support weight equally — neither should spin freely." },
                  { n: "03", t: "Set the backward tilt", d: "Place the level front-to-back on top of the cabinet. Raise the front legs slightly more so the bubble sits 1/4 to 1/2 inch off-center toward the back — this is the manufacturer-specified tilt." },
                  { n: "04", t: "Adjust rear rollers if needed", d: "Most modern units have adjustable rear rollers reached from underneath via a hex socket. Use them to fine-tune; never to substitute for the front legs (which are designed to carry weight)." },
                  { n: "05", t: "Re-test side-to-side after tilting", d: "Tilting back can shift side-to-side level. Recheck and re-adjust until both axes are correct simultaneously." },
                  { n: "06", t: "Reattach the kick plate and reload", d: "Snap the kick plate back on. Reload the door bins evenly — overloading one door can re-introduce a tilt the legs can't compensate for." },
                ].map((s) => (
                  <li
                    key={s.n}
                    className="grid grid-cols-[auto_1fr] gap-5 sm:gap-7 items-start py-5 sm:py-6 border-b border-gray-900"
                  >
                    <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] pt-1 text-[#ff9951]">
                      {s.n}
                    </div>
                    <div>
                      <h3
                        className="text-gray-900"
                        style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                      >
                        {s.t}
                      </h3>
                      <p className="mt-1.5 text-[15px] text-gray-600 leading-[1.55]">{s.d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            <section id="verify" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Verify the install
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  "Doors auto-close from a 45° open position",
                  "Door gaskets contact evenly all the way around",
                  "Top of cabinet is level side-to-side (bubble centered)",
                  "Top of cabinet tilts 1/4–1/2\" back (bubble offset rearward)",
                  "Both front legs bear weight (neither spins freely)",
                  "Ice maker fill cup is centered, no overflow on a test cycle",
                ].map((t) => (
                  <div key={t} className="flex items-start gap-3 p-4 rounded-lg bg-gray-50">
                    <div className="w-6 h-6 rounded-full bg-[#0469f0] text-white flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-[14px] text-gray-700 leading-[1.5]">{t}</span>
                  </div>
                ))}
              </div>
            </section>

            <section id="trouble" className="scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                If it won&apos;t sit right
              </h2>
              <div className="overflow-x-auto rounded-xl border border-gray-900">
                <table className="w-full text-[14px]">
                  <thead>
                    <tr className="bg-gray-900 text-white">
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Symptom
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Likely cause
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Fix
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Door won't auto-close", "Insufficient backward tilt", "Raise front legs another 1/8–1/4\""],
                      ["Door slams hard", "Excess backward tilt", "Lower front legs slightly"],
                      ["Cabinet rocks", "One leg unloaded", "Lower the unloaded leg until it bears weight"],
                      ["Doors out of alignment", "Side-to-side off-level", "Re-level side-to-side, then re-test tilt"],
                      ["Vibration / buzz at compressor cycles", "Cabinet not flat to floor", "Re-level all four corners; check floor flatness"],
                      ["Floor is too uneven for legs", "Out of leg adjustment range", "Use thin shims under the lower side; consider counter-depth model"],
                    ].map((r, i, arr) => (
                      <tr
                        key={r[0]}
                        className={i % 2 ? "bg-gray-50" : "bg-white"}
                        style={{ borderBottom: i < arr.length - 1 ? "1px solid #e5e7eb" : "none" }}
                      >
                        <td className="px-5 py-4 font-extrabold tracking-tight">{r[0]}</td>
                        <td className="px-5 py-4 text-gray-700">{r[1]}</td>
                        <td className="px-5 py-4 text-gray-600">{r[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </article>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Easier installs"
          title="Counter-depth refrigerators"
          description="Shallower footprint and adjustable rear rollers make leveling more forgiving on uneven floors."
          collectionType="counter-depth"
          fallbackCollectionType="french-door"
        />
      </section>
    </ToolPageShell>
  )
}
